import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@apollo/react-hooks'
import { toastr } from 'react-redux-toastr'
import gql from 'graphql-tag'
import { Button } from '@/components/ui/button'
import { Plus } from 'lucide-react'

import * as Mutation from '../../../api/gql/mutation'
import * as Fragments from '../../../api/gql/fragments'
import AgendaEntryRow, { AgendaEntryKind, AgendaEntryPatch, AgendaEntryValue } from './AgendaEntryRow'

interface SubItemNode {
  id: string
  orderIndex: number
  title: string
  notes?: string | null
  kind: 'NOTE' | 'DECISION' | 'RISK' | 'TASK'
  status: string
  dueDate?: string | null
  assignedTo?: { id: string; name: string } | null
  seriesId?: string | null
  previousVersionId?: string | null
  lineageId?: string | null
  lockedAt?: string | null
}

interface ActionItemNode {
  id: string
  title: string
  description?: string | null
  status: string
  priority: string
  dueDate?: string | null
  assignedTo?: { id: string; name: string } | null
  agendaOrderIndex?: number | null
  agendaItem?: { id: string; title: string } | null
  linkedSnag?: { id: string } | null
  linkedDoc?: { id: string } | null
  originOccurrenceNumber?: number | null
  lineageId?: string | null
}

interface Props {
  agendaItemId: string
  subItems: SubItemNode[]
  actionItems: ActionItemNode[]
  users: { id: string; name: string }[]
  onSavingChange?: (saving: boolean) => void
  // MEETINGS.EDIT — gates adding/editing entries (addAgendaSubItem, updateAgendaSubItem,
  // updateActionItem, promoteSubItemToActionItem, demoteActionItemToSubItem).
  canEdit: boolean
  // MEETINGS.DELETE — only used for deleting actionItem-type entries (deleteActionItem).
  canDelete: boolean
}

const AgendaItemEntriesFragment = gql`
  fragment AgendaItemEntriesFragment on AgendaItem {
    id
    subItems {
      ...AgendaSubItemFragment
    }
    actionItems {
      ...ActionItemFragment
    }
  }
  ${Fragments.AgendaSubItemFragment}
  ${Fragments.ActionItemFragment}
`

const AgendaEntriesSection: React.FC<Props> = ({
  agendaItemId,
  subItems,
  actionItems,
  users,
  onSavingChange,
  canEdit,
  canDelete,
}) => {
  const { t } = useTranslation()
  const lastInputRef = useRef<HTMLInputElement | null>(null)
  const focusNextRef = useRef(false)
  const [swappingIds, setSwappingIds] = useState<Set<string>>(new Set())

  const cacheKey = `AgendaItem:${agendaItemId}`

  const [addSubItem, { loading: addingSub }] = useMutation(Mutation.ADD_AGENDA_SUB_ITEM)
  const [updateSubItem, { loading: updatingSub }] = useMutation(Mutation.UPDATE_AGENDA_SUB_ITEM)
  const [removeSubItem, { loading: removingSub }] = useMutation(Mutation.REMOVE_AGENDA_SUB_ITEM)
  const [updateActionItem, { loading: updatingAction }] = useMutation(Mutation.UPDATE_ACTION_ITEM)
  const [deleteActionItem, { loading: deletingAction }] = useMutation(Mutation.DELETE_ACTION_ITEM, {
    refetchQueries: ['meeting'],
  })
  const [promoteToAction, { loading: promoting }] = useMutation(Mutation.PROMOTE_SUB_ITEM_TO_ACTION, {
    refetchQueries: ['meeting'],
  })
  const [demoteToSub, { loading: demoting }] = useMutation(Mutation.DEMOTE_ACTION_TO_SUB_ITEM, {
    refetchQueries: ['meeting'],
  })

  const anySaving =
    addingSub || updatingSub || removingSub || updatingAction || deletingAction || promoting || demoting

  useEffect(() => {
    onSavingChange?.(anySaving)
  }, [anySaving, onSavingChange])

  const entries: AgendaEntryValue[] = useMemo(() => {
    const subs: AgendaEntryValue[] = subItems
      .filter((s) => s.kind !== 'TASK')
      .map((s) => ({
        entityType: 'subItem' as const,
        id: s.id,
        title: s.title,
        notes: s.notes,
        kind: s.kind as 'NOTE' | 'DECISION' | 'RISK',
        status: s.status,
        dueDate: s.dueDate,
        assignedTo: s.assignedTo,
        orderIndex: s.orderIndex,
        seriesId: s.seriesId,
        previousVersionId: s.previousVersionId,
        lineageId: s.lineageId,
        lockedAt: s.lockedAt,
      }))
    const acts: AgendaEntryValue[] = actionItems.map((a) => ({
      entityType: 'actionItem' as const,
      id: a.id,
      title: a.title,
      description: a.description,
      kind: 'TASK' as const,
      status: a.status,
      dueDate: a.dueDate,
      assignedTo: a.assignedTo,
      agendaOrderIndex: a.agendaOrderIndex ?? 0,
      originOccurrenceNumber: a.originOccurrenceNumber,
      lineageId: a.lineageId,
    }))
    const merged = [...subs, ...acts]
    merged.sort((a, b) => {
      const aIdx = a.entityType === 'subItem' ? a.orderIndex : a.agendaOrderIndex
      const bIdx = b.entityType === 'subItem' ? b.orderIndex : b.agendaOrderIndex
      return aIdx - bIdx
    })
    return merged
  }, [subItems, actionItems])

  const lastEntryId = entries.length > 0 ? entries[entries.length - 1].id : null

  useEffect(() => {
    if (!focusNextRef.current) return
    const el = lastInputRef.current
    if (!el) return
    el.focus()
    if (lastEntryId && !String(lastEntryId).startsWith('temp-')) {
      focusNextRef.current = false
    }
  }, [entries.length, lastEntryId])

  const markSwapping = (id: string, on: boolean) => {
    setSwappingIds((prev) => {
      const next = new Set(prev)
      if (on) next.add(id)
      else next.delete(id)
      return next
    })
  }

  const handleAddSubItem = async () => {
    try {
      focusNextRef.current = true
      const tempId = `temp-${Date.now()}`
      const now = new Date().toISOString()
      const maxIdx = entries.reduce((max, e) => {
        const idx = e.entityType === 'subItem' ? e.orderIndex : e.agendaOrderIndex
        return idx > max ? idx : max
      }, -1)
      const nextIndex = maxIdx + 1
      await addSubItem({
        variables: {
          agendaItemId,
          input: {
            title: '',
            kind: 'NOTE',
            status: 'OPEN',
            orderIndex: nextIndex,
          },
        },
        optimisticResponse: {
          __typename: 'Mutation',
          addAgendaSubItem: {
            __typename: 'AgendaSubItem',
            id: tempId,
            orderIndex: nextIndex,
            title: '',
            notes: null,
            kind: 'NOTE',
            status: 'OPEN',
            dueDate: null,
            createdAt: now,
            updatedAt: now,
            assignedTo: null,
          },
        },
        update(cache, { data }) {
          const created = (data as any)?.addAgendaSubItem
          if (!created) return
          const existing: any = cache.readFragment({
            id: cacheKey,
            fragment: AgendaItemEntriesFragment,
            fragmentName: 'AgendaItemEntriesFragment',
          })
          if (!existing) return
          const next = [
            ...(existing.subItems || []).filter((s: any) => !String(s.id).startsWith('temp-')),
            created,
          ]
          cache.writeFragment({
            id: cacheKey,
            fragment: AgendaItemEntriesFragment,
            fragmentName: 'AgendaItemEntriesFragment',
            data: { ...existing, subItems: next },
          })
        },
      })
    } catch (e: any) {
      toastr.error(t('Common_error'), e.message)
    }
  }

  const handlePatch = async (entry: AgendaEntryValue, patch: AgendaEntryPatch) => {
    try {
      if (entry.entityType === 'subItem') {
        const input: any = {}
        if ('title' in patch) input.title = patch.title
        if ('notes' in patch) input.notes = patch.notes
        if ('status' in patch) input.status = patch.status
        if ('dueDate' in patch) input.dueDate = patch.dueDate
        if ('assignedTo' in patch) {
          input.assignedToId = patch.assignedTo ? patch.assignedTo.id : null
        }
        await updateSubItem({ variables: { id: entry.id, input } })
      } else {
        const input: any = {}
        if ('title' in patch) input.title = patch.title
        if ('description' in patch) input.description = patch.description
        if ('status' in patch) input.status = patch.status
        if ('dueDate' in patch) input.dueDate = patch.dueDate
        if ('assignedTo' in patch) {
          input.assignedToId = patch.assignedTo ? patch.assignedTo.id : null
        }
        await updateActionItem({ variables: { id: entry.id, input } })
      }
    } catch (e: any) {
      toastr.error(t('Common_error'), e.message)
    }
  }

  const handleDelete = async (entry: AgendaEntryValue) => {
    try {
      if (entry.entityType === 'subItem') {
        await removeSubItem({
          variables: { id: entry.id },
          optimisticResponse: {
            __typename: 'Mutation',
            removeAgendaSubItem: { __typename: 'AgendaSubItem', id: entry.id },
          },
          update(cache) {
            const existing: any = cache.readFragment({
              id: cacheKey,
              fragment: AgendaItemEntriesFragment,
              fragmentName: 'AgendaItemEntriesFragment',
            })
            if (!existing) return
            cache.writeFragment({
              id: cacheKey,
              fragment: AgendaItemEntriesFragment,
              fragmentName: 'AgendaItemEntriesFragment',
              data: {
                ...existing,
                subItems: (existing.subItems || []).filter((s: any) => s.id !== entry.id),
              },
            })
          },
        })
      } else {
        await deleteActionItem({
          variables: { id: entry.id },
          update(cache) {
            const existing: any = cache.readFragment({
              id: cacheKey,
              fragment: AgendaItemEntriesFragment,
              fragmentName: 'AgendaItemEntriesFragment',
            })
            if (!existing) return
            cache.writeFragment({
              id: cacheKey,
              fragment: AgendaItemEntriesFragment,
              fragmentName: 'AgendaItemEntriesFragment',
              data: {
                ...existing,
                actionItems: (existing.actionItems || []).filter((a: any) => a.id !== entry.id),
              },
            })
          },
        })
      }
    } catch (e: any) {
      toastr.error(t('Common_error'), e.message)
    }
  }

  const handleKindChange = async (entry: AgendaEntryValue, newKind: AgendaEntryKind) => {
    if (entry.kind === newKind) return

    if (entry.entityType === 'subItem' && newKind !== 'TASK') {
      try {
        await updateSubItem({ variables: { id: entry.id, input: { kind: newKind } } })
      } catch (e: any) {
        toastr.error(t('Common_error'), e.message)
      }
      return
    }

    if (entry.entityType === 'subItem' && newKind === 'TASK') {
      markSwapping(entry.id, true)
      try {
        await promoteToAction({
          variables: { id: entry.id },
          update(cache, { data }) {
            const created = (data as any)?.promoteSubItemToActionItem
            if (!created) return
            const existing: any = cache.readFragment({
              id: cacheKey,
              fragment: AgendaItemEntriesFragment,
              fragmentName: 'AgendaItemEntriesFragment',
            })
            if (!existing) return
            cache.writeFragment({
              id: cacheKey,
              fragment: AgendaItemEntriesFragment,
              fragmentName: 'AgendaItemEntriesFragment',
              data: {
                ...existing,
                subItems: (existing.subItems || []).filter((s: any) => s.id !== entry.id),
                actionItems: [...(existing.actionItems || []), created],
              },
            })
          },
        })
        toastr.success(t('Common_success'), t('AgendaEntry_promoted_toast'))
      } catch (e: any) {
        toastr.error(t('Common_error'), e.message)
      } finally {
        markSwapping(entry.id, false)
      }
      return
    }

    if (entry.entityType === 'actionItem' && newKind !== 'TASK') {
      markSwapping(entry.id, true)
      try {
        await demoteToSub({
          variables: { id: entry.id, kind: newKind },
          update(cache, { data }) {
            const created = (data as any)?.demoteActionItemToSubItem
            if (!created) return
            const existing: any = cache.readFragment({
              id: cacheKey,
              fragment: AgendaItemEntriesFragment,
              fragmentName: 'AgendaItemEntriesFragment',
            })
            if (!existing) return
            cache.writeFragment({
              id: cacheKey,
              fragment: AgendaItemEntriesFragment,
              fragmentName: 'AgendaItemEntriesFragment',
              data: {
                ...existing,
                subItems: [...(existing.subItems || []), created],
                actionItems: (existing.actionItems || []).filter((a: any) => a.id !== entry.id),
              },
            })
          },
        })
        toastr.success(t('Common_success'), t('AgendaEntry_demoted_toast'))
      } catch (e: any) {
        toastr.error(t('Common_error'), e.message)
      } finally {
        markSwapping(entry.id, false)
      }
      return
    }
  }

  return (
    <div className="space-y-2">
      {entries.map((entry, idx) => (
        <AgendaEntryRow
          key={`${entry.entityType}:${entry.id}`}
          value={entry}
          users={users}
          inputRef={idx === entries.length - 1 ? lastInputRef : undefined}
          swapping={swappingIds.has(entry.id)}
          canEdit={canEdit}
          canDelete={canDelete}
          onPatch={(patch) => handlePatch(entry, patch)}
          onKindChange={(newKind) => handleKindChange(entry, newKind)}
          onDelete={() => handleDelete(entry)}
        />
      ))}

      {canEdit && (
        <Button
          variant="outline"
          size="sm"
          onClick={handleAddSubItem}
          disabled={addingSub}
        >
          <Plus className="h-4 w-4" />
          {t('AgendaSubItem_add_button')}
        </Button>
      )}
    </div>
  )
}

export default AgendaEntriesSection
